import { useState } from "react";

/**
 * Znacznik operatora (OSD) projektu ZE — od niego zależy dobór szablonów, wnioskodawca
 * wypisu i sposób opisywania prac, więc musi być widoczny na pierwszy rzut oka.
 *
 * Logo (public/operators/<operator>.png) niesie już nazwę marki, więc przy wczytanym
 * logu nie dublujemy jej tekstem. Barwa ramki i paska jest firmowa (TAURON — magenta,
 * ENERGA — zieleń), pobrana wprost z logotypów. Gdy pliku brakuje albo operator jest
 * nieznany, zostaje czytelny kafel z samą nazwą — nic się nie chowa.
 */
type OperatorStyle = { label: string; accent: string };

const OPERATORS: Record<string, OperatorStyle> = {
  tauron: { label: "TAURON", accent: "#E0007A" },
  energa: { label: "ENERGA", accent: "#3FA535" },
};

const OTHER: OperatorStyle = { label: "INNY OSD", accent: "var(--color-border-strong)" };

export function OperatorBadge({
  operator,
  operatorDisplay,
  size = "md",
}: {
  operator: string;
  /** Pełna nazwa z backendu (np. „TAURON Dystrybucja") — ląduje w tooltipie. */
  operatorDisplay?: string;
  size?: "sm" | "md";
}) {
  const [logoOk, setLogoOk] = useState(true);
  const known = OPERATORS[operator];
  const style = known ?? OTHER;
  const showLogo = !!known && logoOk;
  // STAŁY kafel: logotypy mają różne proporcje (TAURON 3,4:1, ENERGA 3,9:1), a znacznik
  // stoi obok tytułu, który potrafi zająć trzy wiersze — bez sztywnego pudełka i zakazu
  // kurczenia raz jest duży, raz zgnieciony. shrink-0 = tytuł zawija się, znacznik nie.
  const box = size === "sm" ? { width: 96, height: 26 } : { width: 132, height: 36 };

  return (
    <span
      className="inline-flex shrink-0 items-center justify-center bg-[var(--color-bg-surface)] border px-2"
      style={{ ...box, borderColor: style.accent, borderLeftWidth: 4 }}
      title={
        operatorDisplay
          ? `Operator projektu: ${operatorDisplay} — w tym trybie dobierane są szablony, wnioskodawca wypisów i opisy prac.`
          : "Operator (OSD) projektu — decyduje o doborze szablonów i opisów prac."
      }
    >
      {showLogo ? (
        <img
          src={`/operators/${operator}.png`}
          alt={style.label}
          className="max-h-full max-w-full"
          style={{ objectFit: "contain" }}
          onError={() => setLogoOk(false)}
        />
      ) : (
        <span
          className={`font-mono font-bold uppercase tracking-wider ${
            size === "sm" ? "text-[10px]" : "text-xs"
          }`}
          style={{ color: known ? style.accent : "var(--color-text-secondary)" }}
        >
          {style.label}
        </span>
      )}
    </span>
  );
}
